import { apiDelete, apiDownload, apiGet, apiPatch, apiRoutes, apiUpload, buildApiRoute } from "./entityApiShared.js";
import type { EntityRecord } from "../types/api.types.js";
import { requireCmtsIdentifier, type EvidenceUploadMetadata } from "../core/payloads/cmtsTypedPayloads.js";
import type { AxiosProgressEvent } from "axios";

export type EvidenceDto = EntityRecord;
export type EvidenceUploadResult = EntityRecord;
export type EvidenceDownloadTokenResult = EntityRecord & { readonly token?: string; readonly downloadToken?: string; readonly url?: string };

export type EvidenceUploadOptions = {
  readonly metadata?: EvidenceUploadMetadata | undefined;
  readonly signal?: AbortSignal | undefined;
  readonly onUploadProgress?: ((event: AxiosProgressEvent) => void) | undefined;
};

export const evidenceApi = {
  list: (complaintId: string, signal?: AbortSignal): Promise<EvidenceDto[]> => {
    const safeComplaintId = requireCmtsIdentifier(complaintId, "complaintId");
    return apiGet<EvidenceDto[]>(buildApiRoute(apiRoutes.complaints.evidence.list, { id: safeComplaintId }), { signal });
  },
  metadata: (complaintId: string, evidenceId: string, signal?: AbortSignal): Promise<EvidenceDto> => {
    const safeComplaintId = requireCmtsIdentifier(complaintId, "complaintId");
    const safeEvidenceId = requireCmtsIdentifier(evidenceId, "evidenceId");
    return apiGet<EvidenceDto>(buildApiRoute(apiRoutes.complaints.evidence.metadata, { id: safeComplaintId, evidenceId: safeEvidenceId }), { signal });
  },
uploadFile(
  complaintId: string,
  file: File,
  options?: EvidenceUploadOptions,
): Promise<EvidenceUploadResult> {
  const safeComplaintId = requireCmtsIdentifier(
    complaintId,
    "complaintId",
  );

  if (!file.name.trim()) {
    throw new Error("Evidence fileName is required.");
  }

  const metadata = options?.metadata;

  const confidentiality = (
    metadata?.confidentiality ?? ""
  ).trim();

  if (!confidentiality) {
    throw new Error(
      "Evidence confidentiality is required.",
    );
  }

  const form = new FormData();

  // Actual binary file
  form.append("file", file, file.name);

  // Evidence metadata
  form.append(
    "fileName",
    metadata?.fileName?.trim() || file.name,
  );

  form.append(
    "fileType",
    metadata?.fileType?.trim() || file.type,
  );

  form.append(
    "sizeBytes",
    String(
      metadata?.sizeBytes ?? file.size,
    ),
  );

  // Backend confidentiality reference ID
  form.append(
    "confidentiality",
    confidentiality,
  );

  // Optional description
  if (metadata?.description?.trim()) {
    form.append(
      "description",
      metadata.description.trim(),
    );
  }

  return apiUpload<EvidenceUploadResult>(
    buildApiRoute(
      apiRoutes.complaints.evidence.upload,
      {
        id: safeComplaintId,
      },
    ),
    form,
    {
      signal: options?.signal,
      onUploadProgress:
        options?.onUploadProgress,
    },
  );
},
  upload: (complaintId: string, form: FormData, signal?: AbortSignal): Promise<EvidenceUploadResult> => {
    const safeComplaintId = requireCmtsIdentifier(complaintId, "complaintId");
    return apiUpload<EvidenceUploadResult>(buildApiRoute(apiRoutes.complaints.evidence.upload, { id: safeComplaintId }), form, { signal });
  },
  downloadToken: (complaintId: string, evidenceId: string, signal?: AbortSignal): Promise<EvidenceDownloadTokenResult> => {
    const safeComplaintId = requireCmtsIdentifier(complaintId, "complaintId");
    const safeEvidenceId = requireCmtsIdentifier(evidenceId, "evidenceId");
    return apiGet<EvidenceDownloadTokenResult>(buildApiRoute(apiRoutes.complaints.evidence.downloadToken, { id: safeComplaintId, evidenceId: safeEvidenceId }), { signal });
  },
  downloadBlob: (token: string, signal?: AbortSignal): Promise<Blob> => {
    const safeToken = requireCmtsIdentifier(token, "downloadToken");
    return apiDownload(buildApiRoute(apiRoutes.complaints.evidence.downloadBlob, { token: safeToken }), { signal });
  },
  download: (complaintId: string, evidenceId: string, signal?: AbortSignal): Promise<Blob> => {
    const safeComplaintId = requireCmtsIdentifier(complaintId, "complaintId");
    const safeEvidenceId = requireCmtsIdentifier(evidenceId, "evidenceId");
    return apiDownload(buildApiRoute(apiRoutes.complaints.evidence.download, { id: safeComplaintId, evidenceId: safeEvidenceId }), { signal });
  },
  deactivate: async (complaintId: string, evidenceId: string, reason?: string): Promise<void> => {
    const safeComplaintId = requireCmtsIdentifier(complaintId, "complaintId");
    const safeEvidenceId = requireCmtsIdentifier(evidenceId, "evidenceId");
    await apiPatch<void>(buildApiRoute(apiRoutes.complaints.evidence.deactivate, { id: safeComplaintId, evidenceId: safeEvidenceId }), reason ? { reason } : undefined);
  },
  delete: async (complaintId: string, evidenceId: string): Promise<void> => {
    const safeComplaintId = requireCmtsIdentifier(complaintId, "complaintId");
    const safeEvidenceId = requireCmtsIdentifier(evidenceId, "evidenceId");
    await apiDelete<void>(buildApiRoute(apiRoutes.complaints.evidence.delete, { id: safeComplaintId, evidenceId: safeEvidenceId }));
  },
};
